/* global BigInt */
/**
 * User Position Hook
 * Manages user's position state for the active game
 * Recomputes live P&L when game state or wallet balance changes
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSolanaLive } from './useSolanaLive';
import { refreshPositionForActiveGame } from '../services/positionService';
import { getSellQuote } from '../solana/quotes/sellQuote';
import {
  calculateUnrealizedPnL,
  calculateUnrealizedPnLPercent,
} from '../helpers/positionMath';

/**
 * Hook to manage user's position for the active game
 * @param {string} walletAddress - connected wallet address
 * @param {string} roundTokenAmountAtomic - wallet's token balance for active game
 * @returns {object} position state and actions
 */
export function useUserPosition(walletAddress, roundTokenAmountAtomic) {
  const { currentGameState, globalConfig } = useSolanaLive();
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentValue, setCurrentValue] = useState(null);
  const [unrealizedPnL, setUnrealizedPnL] = useState(null);
  const [unrealizedPnLPercent, setUnrealizedPnLPercent] = useState(null);
  const lastGameIdRef = useRef(null);
  const lastWalletRef = useRef(null);

  const currentGameId = currentGameState?.game_id || '';

  /**
   * Refresh position from database
   */
  const refreshPosition = useCallback(async () => {
    if (!walletAddress || !currentGameId) {
      setPosition(null);
      setCurrentValue(null);
      setUnrealizedPnL(null);
      setUnrealizedPnLPercent(null);
      setLoading(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const positionData = await refreshPositionForActiveGame(walletAddress, currentGameId);
      setPosition(positionData);

      // Compute current value and unrealized P&L if we have a position
      if (positionData && positionData.token_balance) {
        computeLivePnL(positionData);
      } else {
        setCurrentValue(null);
        setUnrealizedPnL(null);
        setUnrealizedPnLPercent(null);
      }
    } catch (err) {
      console.error('Failed to refresh position:', err);
      setError(err?.message || 'Failed to load position');
      setPosition(null);
      setCurrentValue(null);
      setUnrealizedPnL(null);
      setUnrealizedPnLPercent(null);
    } finally {
      setLoading(false);
    }
  }, [walletAddress, currentGameId]);

  /**
   * Compute live P&L based on current game state and quote
   */
  const computeLivePnL = useCallback(
    (positionData) => {
      if (!positionData || !currentGameState || !globalConfig) {
        setCurrentValue(null);
        setUnrealizedPnL(null);
        setUnrealizedPnLPercent(null);
        return;
      }

      const tokenBalance = String(positionData.token_balance || 0);
      const costBasis = String(positionData.cost_basis_lamports || 0);

      // If no tokens, no unrealized P&L
      if (window.BigInt(tokenBalance) === 0n) {
        setCurrentValue('0');
        setUnrealizedPnL(null);
        setUnrealizedPnLPercent(null);
        return;
      }

      try {
        // Use existing sell quote logic to get current value
        // Convert token balance from atoms to display format for quote function
        const decimals = currentGameState.decimals || 6;
        const tokenBalanceBigInt = window.BigInt(tokenBalance);
        const wholePart = tokenBalanceBigInt / window.BigInt(10 ** decimals);
        const fractionalPart = tokenBalanceBigInt % window.BigInt(10 ** decimals);
        const tokenAmountDisplay = `${wholePart}.${fractionalPart.toString().padStart(decimals, '0')}`;

        const sellQuote = getSellQuote({
          globalConfig,
          currentGameState,
          tokenAmount: tokenAmountDisplay,
        });

        const currentValueLamports = sellQuote.netLamportsOut;

        setCurrentValue(currentValueLamports);

        // Calculate unrealized P&L
        const unrealizedPnLValue = calculateUnrealizedPnL(currentValueLamports, costBasis);
        setUnrealizedPnL(unrealizedPnLValue);

        // Calculate unrealized P&L percent
        if (unrealizedPnLValue !== null) {
          const unrealizedPnLPercentValue = calculateUnrealizedPnLPercent(unrealizedPnLValue, costBasis);
          setUnrealizedPnLPercent(unrealizedPnLPercentValue);
        } else {
          setUnrealizedPnLPercent(null);
        }
      } catch (err) {
        console.error('Failed to compute live P&L:', err);
        setCurrentValue(null);
        setUnrealizedPnL(null);
        setUnrealizedPnLPercent(null);
      }
    },
    [currentGameState, globalConfig]
  );

  /**
   * Initial load when wallet or game changes
   */
  useEffect(() => {
    // Refresh if wallet or game changed
    const walletChanged = walletAddress !== lastWalletRef.current;
    const gameChanged = currentGameId !== lastGameIdRef.current;

    if (walletChanged || gameChanged) {
      lastWalletRef.current = walletAddress;
      lastGameIdRef.current = currentGameId;
      refreshPosition();
    }
  }, [walletAddress, currentGameId, refreshPosition]);

  /**
   * Recompute live P&L when game state or token balance changes
   */
  useEffect(() => {
    if (position) {
      computeLivePnL(position);
    }
  }, [currentGameState, globalConfig, roundTokenAmountAtomic, position, computeLivePnL]);

  return {
    position,
    loading,
    error,
    currentValue,
    unrealizedPnL,
    unrealizedPnLPercent,
    refreshPosition,
  };
}
